/**
 * Bare PSI wrapper around react-aria-components' `OverlayArrow`. Internal arrow
 * primitive shared by `BasePopover` and `BaseTooltip`, not for direct use in
 * product code.
 */
import { OverlayArrow } from 'react-aria-components';

const TIP_RADIUS = 2;
// Pull the arrow into the popover body by this many px so the rounded base
// corners sit under the popover fill and only the tip-facing portion protrudes.
// Halved from the Figma popover-arrow spec (32×24 with OVERLAP 15) so the
// rotated arrow fits inside a tooltip's narrow vertical edge; pending design
// sync with Tori, this reduced size now applies to popovers too.
const OVERLAP = 7;

interface BaseOverlayArrowProps {
    className: string;
    width?: number;
    height?: number;
    fill?: string;
}

export function BaseOverlayArrow({ className, width = 16, height = 12, fill }: BaseOverlayArrowProps) {
    // Inset the polygon by TIP_RADIUS; stroke-linejoin="round" with
    // stroke-width = 2·TIP_RADIUS expands each side outward by TIP_RADIUS and
    // rounds every join with an arc of that radius. The two base corners sit
    // under the popover body, so only the tip's rounding is visible.
    const r = TIP_RADIUS;
    const points = `${r},${r} ${width - r},${r} ${width / 2},${height - r}`;
    return (
        // zIndex:-1 on the OverlayArrow wrapper (not the inner SVG) puts it in
        // the popover's negative-z paint layer: above the popover's background
        // (so the tip outside the popover box is still visible as the pointer)
        // but below in-flow text (so the portion overlapping inside the popover
        // doesn't obscure content). Must be on the wrapper because RAC gives it
        // `transform: translateX(-50%)` which creates its own stacking context
        // — a z-index on the inner SVG would be trapped inside that context and
        // still paint above siblings' text. Depends on the popover body
        // establishing a stacking context (see `.popover` / `.tooltip` →
        // `isolation: isolate`) so this negative-z stays contained.
        <OverlayArrow style={{ zIndex: -1 }}>
            {({ placement }) => {
                const rotation =
                    placement === 'bottom'
                        ? 'rotate(180deg)'
                        : placement === 'left'
                          ? 'rotate(-90deg)'
                          : placement === 'right'
                            ? 'rotate(90deg)'
                            : '';
                // Applied right-to-left: first translateY(-OVERLAP) in element
                // space (moves the arrow toward the popover before any
                // rotation), then the placement rotation maps that
                // element-space shift onto the correct visual axis for all four
                // placements.
                const transform = `${rotation} translateY(-${OVERLAP}px)`.trim();
                return (
                    <svg className={className} height={height} style={{ transform }} viewBox={`0 0 ${width} ${height}`} width={width}>
                        <polygon fill={fill} points={points} stroke={fill} strokeLinejoin='round' strokeWidth={r * 2} />
                    </svg>
                );
            }}
        </OverlayArrow>
    );
}
